import React, { FC, useState, useContext, useEffect, MouseEvent } from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import { UserContext } from "@ipd/js-common";
import { decode } from "jsonwebtoken";
import { sortBy } from "lodash";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import { therapeuticAreas } from "../typescript/types";
import { ClinicalPipeline } from "../components/ClinicalPipeline";
import { useLazyQuery } from "@apollo/client";
import { THERAPEUTIC_AREAS } from "../apollo/therapeuticAreas";
import { utils } from "../functions";
import { TherapeuticAreaContext } from "../context";
import NoAccessModal from "../components/Modals/NoAccess";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    clinicalPipelineContent: {
      paddingTop: "15px",
    },
    taSelectContainer: {
      minWidth: 300,
    },
    disabled: {
      filter: "brightness(0.85)",
    },
  })
);

const TherapeuticArea: FC = () => {
  const classes = useStyles();
  const { therapeuticArea } = useParams<{ therapeuticArea: string }>();
  const { token, hasClaim } = useContext(UserContext);
  const [therapeuticAreaContext, setThereapeuticAreaContext] = useContext(TherapeuticAreaContext);
  const [taArray, setTaArray] = useState<therapeuticAreas.TherapeuticAreaType[]>([]);
  const [ta, setTa] = useState(therapeuticArea);
  const [permittedTaSlugs, setPermittedTaSlugs] = useState<string[]>([]);
  const [getTherapeuticAreas, { loading: loadingTas, data: tasResponseData, error: tasResponseError }] =
    useLazyQuery(THERAPEUTIC_AREAS);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setTa(event.target.value as string);
    setThereapeuticAreaContext(event.target.value as string);
  };

  const getPermittedTaSlugs = (): string[] => {
    const taSlugs: string[] = [];
    const jwtToken: any = decode(token);
    for (const ta of utils.ensureArray(jwtToken["http://secure.ipdanalytics.com/claims/therapeuticInsightsArea"])) {
      const [taSlug, taClaimPrefix] = ta.split(" | ");
      const pharmaClaimName = `${taClaimPrefix}_Pharma_USA`; // eg: TherapeuticInsights_Oncology_Pharma_EP
      const biologicClaimName = `${taClaimPrefix}_Biologic_USA`; // eg: TherapeuticInsights_Oncology_Biologic_EP
      const hasAccessToTherapeuticArea =
        hasClaim(pharmaClaimName, "LciClinicalPipeline") || hasClaim(biologicClaimName, "LciClinicalPipeline");
      if (hasAccessToTherapeuticArea) {
        taSlugs.push(taSlug);
      }
    }
    return taSlugs;
  };

  useEffect(() => {
    setThereapeuticAreaContext(therapeuticArea);
    getTherapeuticAreas();
    const taSlugs = getPermittedTaSlugs();
    setPermittedTaSlugs(taSlugs);
  }, []);

  useEffect(() => {
    if (tasResponseData && tasResponseData?.therapeuticAreas) {
      const tempArray = tasResponseData.therapeuticAreas.reduce(
        (acc: therapeuticAreas.TherapeuticAreaType[], current: any) => {
          if (permittedTaSlugs.includes(current.slug)) return [...acc, { name: current.name, value: current.slug }];
          return [...acc];
        },
        []
      );
      setTaArray(sortBy(tempArray, ["name"]));
    }
  }, [tasResponseData]);

  if (tasResponseError) return <pre>{JSON.stringify(tasResponseError, null, 1)}</pre>;

  return (
    <Grid container>
      <Grid container item xs={12}>
        {loadingTas ? (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {taArray.length > 0 ? (
              <>
                <Grid item xs={12}>
                  <FormControl className={classes.taSelectContainer}>
                    <InputLabel id="therapeutic-area-select-label">Therapeutic Area</InputLabel>
                    <Select
                      labelId="therapeutic-area-select-label"
                      id="therapeutic-area-select"
                      value={ta}
                      onChange={handleChange}
                    >
                      {taArray.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} className={classes.clinicalPipelineContent}>
                  <ClinicalPipeline />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <NoAccessModal />
                </Grid>
                <Grid
                  item
                  xs={12}
                  className={`${classes.clinicalPipelineContent} ${classes.disabled}`}
                  onClickCapture={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <ClinicalPipeline />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default TherapeuticArea;
